import type { LiveMapBackgroundData } from '../types/LiveMap.types';
import {
  parseMapAreasFromWork,
  parseMapFeatures,
} from '../../../../components/MachineDetailsRobotPanel/MachineDetailsRobotSchedule/utils/mapAreaParser';
import { fetchTextOrNull } from './fetchText';
import { getActiveCleaningZoneIds } from './cleaningZones';
import { mapGeoreferenceToMapInfo } from './mapCoordinates';
import type { RobotCleaningControlService } from 'app/modules/machine-inventory/RobotCleaningControlService';
import { getCurrentCleaningTask } from 'app/modules/machine-inventory/utils/robotCleaningUtils';

/** B50's background map, georeference and planned path, from the Route. */
export const getB50MapData = async (
  robotCleaningControlService: RobotCleaningControlService,
  routeId: string | undefined,
  materialNumber: string,
  serialNumber: string
): Promise<LiveMapBackgroundData> => {
  if (!routeId) {
    throw new Error('getB50MapData: no route id');
  }

  const route = (
    await robotCleaningControlService.getRouteLiveMapFields({
      robotRouteKey: { materialNumber, serialNumber, robotRouteId: routeId },
    })
  )?.route;
  const { map, expectedPath } = route ?? {};

  if (!map?.image) {
    throw new Error(`getB50MapData: route ${routeId} has no map`);
  }

  const { resolution, origin } = map.metadata;

  if (!resolution || origin.length < 2) {
    throw new Error(`getB50MapData: route ${routeId} has an invalid map georeference`);
  }

  // The image's pixel size is unknown here: MapView takes it from the loaded <img> for B50.
  return {
    mapImageUrl: map.image,
    mapInfo: {
      resolution,
      origin: [origin[0], origin[1], origin[2] ?? 0],
      imageWidthPx: 0,
      imageHeightPx: 0,
      verticalFlip: true,
    },
    plannedPath: expectedPath ? expectedPath.x.map((x, index) => ({ x, y: expectedPath.y[index] })) : [],
  };
};

type Cv50MapData = Omit<LiveMapBackgroundData, 'plannedPath'>;

const parseMapConfig = (rawConfig: string | null | undefined): { Resolution?: unknown } => {
  if (!rawConfig) {
    return {};
  }

  try {
    return JSON.parse(rawConfig) ?? {};
  } catch (error) {
    console.error(error);

    return {};
  }
};

/** CV50's map comes from its current cleaning task's mapId (no scheduleId available). */
export const getCv50MapData = async (
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string
): Promise<Cv50MapData> => {
  // The scope (and the zones it covers) lives on the current cleaning report, not the task.
  const [task, activeZones] = await Promise.all([
    getCurrentCleaningTask(robotCleaningControlService, materialNumber, serialNumber),
    getActiveCleaningZoneIds(robotCleaningControlService, materialNumber, serialNumber),
  ]);
  const mapId = task?.mapId;

  if (!mapId) {
    throw new Error('getCv50MapData: no current cleaning task with a map');
  }

  const map = (await robotCleaningControlService.getMapById({ id: mapId }))?.map;

  if (!map?.image || !map.width || !map.height) {
    throw new Error(`getCv50MapData: map ${mapId} is missing image/width/height`);
  }

  // config.Resolution is millimetres-per-pixel (see RawMapConfig).
  // A malformed config must not take the whole map down: fall back to the default resolution.
  const config = parseMapConfig(map.jsonData?.jsonContent?.config);
  const resolutionMm = typeof config.Resolution === 'number' ? config.Resolution : 50;

  // Inline JSON first, then the presigned URL (as useMapAreas does).
  const jsonContent = map.jsonData?.jsonContent;
  const work = jsonContent?.work || (await fetchTextOrNull(map.work));
  const functionJson = jsonContent?.function || (await fetchTextOrNull(map.function));

  return {
    mapImageUrl: map.image,
    mapInfo: mapGeoreferenceToMapInfo(resolutionMm, map.width, map.height),
    cleaningZones: parseMapAreasFromWork(work, map.width, map.height, jsonContent?.config),
    cleaningZoneFeatures: parseMapFeatures(work, functionJson, map.width, map.height, jsonContent?.config),
    // Only for the task's own map: a report left over from another map would highlight wrong zones.
    activeCleaningZoneIds:
      activeZones && (!activeZones.mapId || activeZones.mapId === mapId) ? activeZones.zoneIds : null,
  };
};
